// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Check, ChevronDown, Circle, Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Step } from "@/features/assistant/types";
import { cn } from "@/lib/utils";

function StepRow({ step, live }: { step: Step; live: boolean }) {
  // A step still "running" when the answer is over was cut off (Stop, error, reload): not done.
  const status = step.status === "done" ? "done" : live ? "running" : "interrupted";
  return (
    <li data-status={status} className="flex items-center gap-1.5 text-muted-foreground text-xs">
      {status === "running" && <Loader2 className="size-3 animate-spin text-primary" />}
      {status === "done" && <Check className="size-3 text-primary" />}
      {status === "interrupted" && <Circle className="size-2 fill-current text-muted-foreground/60" />}
      <span className={cn(status === "running" && "assistant-shimmer")}>{step.label}</span>
    </li>
  );
}

/** Live while the answer runs; afterwards collapsed to the last step, expandable. */
export function StepList({ steps, live }: { steps: Step[]; live: boolean }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  if (steps.length === 0) return null;
  const last = steps[steps.length - 1] as Step;

  if (live || expanded || steps.length === 1) {
    return (
      <div className="mb-2">
        <ul className="space-y-1">
          {steps.map((step) => (
            <StepRow key={step.id} step={step} live={live} />
          ))}
        </ul>
        {!live && steps.length > 1 && (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="mt-1 text-muted-foreground text-xs underline-offset-2 hover:underline"
          >
            {t("assistant.steps.hide")}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="mb-2 flex items-center gap-1">
      <ul>
        <StepRow step={last} live={false} />
      </ul>
      <button
        type="button"
        aria-label={t("assistant.steps.show")}
        onClick={() => setExpanded(true)}
        className="rounded p-0.5 text-muted-foreground hover:bg-muted"
      >
        <ChevronDown className="size-3" />
      </button>
    </div>
  );
}
